import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["document", "branding"]
  static values = { document: String, branding: String, searchBase: String }
  backgroundKeys = ["default", "surface", "tint", "primary", "accent", "secondary"]

  connect() {
    try { this.state = JSON.parse(this.documentValue) } catch { this.state = { blocks: [] } }
    try { this.brandingState = JSON.parse(this.brandingValue) } catch { this.brandingState = {} }
    try { this.previewAlumniMeta = JSON.parse(this.element.querySelector(".public-preview")?.dataset.previewAlumniRecords || "{}") } catch { this.previewAlumniMeta = {} }
    this.searchTimers = new WeakMap()
    this.previewLabelDefaults = Object.fromEntries([...this.element.querySelectorAll("[data-preview-label-key]")].map((node) => [node.dataset.previewLabelKey, node.textContent.trim()]))
    this.renderSearchSelections()
  }

  submit() {
    this.documentTarget.value = JSON.stringify(this.state)
    const brandingInput = this.element.querySelector('input[name="branding"]')
    if (brandingInput) brandingInput.value = JSON.stringify(this.brandingState)
  }

  markDirty() {
    const pill = this.element.querySelector(".public-editor__draft-pill")
    if (pill) pill.textContent = "Draft · unpublished changes"
  }

  change(event) {
    const path = event.currentTarget.dataset.editorPath?.split(".") || []
    if (!path.length) return
    const block = this.state.blocks.find((item) => item.id === path[0])
    let target = block || this.state
    const valuePath = block ? path.slice(1) : path
    valuePath.slice(0, -1).forEach((key) => { target[key] ||= {}; target = target[key] })
    target[valuePath[valuePath.length - 1]] = event.currentTarget.value
    this.updatePreview(path)
    this.renderPreviewSelections()
    this.markDirty()
    this.documentTarget.value = JSON.stringify(this.state)
  }

  switchTab(event) {
    const tab = event.currentTarget.dataset.editorTab
    this.element.querySelectorAll(".public-editor__tabs button").forEach((button) => button.classList.toggle("is-active", button === event.currentTarget))
    this.element.querySelectorAll("[data-editor-tab-panel]").forEach((panel) => { panel.hidden = panel.dataset.editorTabPanel !== tab })
    this.close()
  }

  changeBrandColor(event) {
    const key = event.currentTarget.dataset.brandColor
    const value = event.currentTarget.value.trim().toLowerCase()
    if (!key || !/^#[0-9a-f]{6}$/.test(value)) return
    this.brandingState[key] = value
    this.element.querySelectorAll(`[data-brand-color="${key}"]`).forEach((input) => { if (input !== event.currentTarget) input.value = value })
    const preview = this.element.querySelector(".public-preview")
    const variableMap = { primary: ["--pub-navy"], accent: ["--pub-orange"], secondary: ["--pub-blue"], surface: ["--pub-bg", "--pub-surface"], tint: ["--pub-tint"], heading: ["--pub-ink"], body: ["--pub-muted"], line: ["--pub-line"] }
    variableMap[key]?.forEach((variable) => preview?.style.setProperty(variable, value))
    this.markDirty()
    const brandingInput = this.element.querySelector('input[name="branding"]')
    if (brandingInput) brandingInput.value = JSON.stringify(this.brandingState)
  }

  previewBanner(event) {
    const file = event.currentTarget.files?.[0]
    const hero = this.element.querySelector('[data-preview-block-id="hero"]')
    if (!file || !hero) return
    if (this.bannerPreviewUrl) URL.revokeObjectURL(this.bannerPreviewUrl)
    this.bannerPreviewUrl = URL.createObjectURL(file)
    let image = hero.querySelector("[data-preview-hero-image]")
    if (!image) {
      image = document.createElement("img")
      image.dataset.previewHeroImage = "true"
      image.className = "public-preview__hero-image"
      image.alt = ""
      hero.prepend(image)
    }
    image.src = this.bannerPreviewUrl
    hero.classList.add("public-preview__hero--image")
    hero.querySelector("[data-preview-hero-overlay]")?.classList.remove("is-hidden")
    this.markDirty()
  }

  open(event) {
    const id = event.currentTarget.dataset.blockId
    const panel = this.element.querySelector(`[data-editor-panel="${id}"]`)
    const isOpen = panel && !panel.hidden
    this.close()
    if (!isOpen && panel) {
      panel.hidden = false
      panel.querySelector("input,textarea")?.focus()
    }
  }

  close() { this.element.querySelectorAll("[data-editor-panel]").forEach((panel) => { panel.hidden = true }) }

  toggle(event) {
    const block = this.state.blocks.find((item) => item.id === event.currentTarget.dataset.blockId)
    if (!block) return
    block.enabled = block.enabled === false
    const row = this.element.querySelector(`[data-block-row="${block.id}"]`)
    row?.classList.toggle("is-hidden", !block.enabled)
    event.currentTarget.classList.toggle("is-on", block.enabled)
    event.currentTarget.setAttribute("aria-checked", block.enabled)
    const previewBlock = this.element.querySelector(`[data-preview-block-id="${block.id}"]`)
    previewBlock?.classList.toggle("is-hidden", !block.enabled)
    this.element.querySelector(`[data-preview-nav-block-id="${block.id}"]`)?.classList.toggle("is-hidden", !block.enabled)
    this.markDirty()
    this.documentTarget.value = JSON.stringify(this.state)
  }

  moveUp(event) { this.move(event.currentTarget.dataset.blockId, -1) }
  moveDown(event) { this.move(event.currentTarget.dataset.blockId, 1) }

  move(id, direction) {
    const index = this.state.blocks.findIndex((item) => item.id === id)
    const next = index + direction
    if (index < 0 || next < 0 || next >= this.state.blocks.length) return
    const blocks = this.state.blocks
    ;[blocks[index], blocks[next]] = [blocks[next], blocks[index]]
    this.syncEditorOrder()
    this.syncPreviewOrder()
    this.markDirty()
    this.documentTarget.value = JSON.stringify(this.state)
  }

  toggleLibrary() {
    const library = this.element.querySelector("[data-editor-library]")
    if (library) library.hidden = !library.hidden
  }

  addBlock(event) {
    const type = event.currentTarget.dataset.addType || "quote"
    const id = `custom-${Date.now()}`
    const label = type.charAt(0).toUpperCase() + type.slice(1)
    const block = { id, type, enabled: true, settings: type === "companies" ? { kicker: "Companies", heading: "Where our alumni work", organization_ids: [] } : { kicker: label, heading: label, body: "" } }
    const joinIndex = this.state.blocks.findIndex((item) => item.type === "join")
    this.state.blocks.splice(joinIndex < 0 ? this.state.blocks.length : joinIndex, 0, block)
    this.appendEditorBlock(block)
    this.appendPreviewBlock(block)
    this.syncEditorOrder()
    this.syncPreviewOrder()
    this.markDirty()
    this.documentTarget.value = JSON.stringify(this.state)
    this.element.querySelector("[data-editor-library]")?.setAttribute("hidden", "hidden")
    this.open({ currentTarget: { dataset: { blockId: id } } })
  }

  appendEditorBlock(block) {
    const list = this.element.querySelector(".public-editor__block-list")
    if (!list) return
    const name = block.type.charAt(0).toUpperCase() + block.type.slice(1)
    const descriptions = { companies: "Organizations where alumni have worked", banner: "A full-width visual banner", quote: "A testimonial from your community", give: "Donation or fundraising call to action", questions: "A short FAQ list", contact: "Email, address and office hours" }
    const row = document.createElement("div")
    row.className = "public-editor__block-row"
    row.dataset.blockRow = block.id
    row.innerHTML = `<div class="public-editor__move"><button type="button" data-action="public-page-editor#moveUp" data-block-id="${block.id}" aria-label="Move up">⌃</button><button type="button" data-action="public-page-editor#moveDown" data-block-id="${block.id}" aria-label="Move down">⌄</button></div><button type="button" class="public-editor__block-open" data-action="public-page-editor#open" data-block-id="${block.id}"><strong>${name}</strong><small>${descriptions[block.type] || "Content block"}</small></button><button type="button" class="editor-switch is-on" role="switch" aria-checked="true" data-action="public-page-editor#toggle" data-block-id="${block.id}"><span></span></button>`
    const panel = document.createElement("div")
    panel.className = "public-editor__panel"
    panel.dataset.editorPanel = block.id
    panel.hidden = true
    const fields = document.createElement("div")
    fields.className = "public-editor__panel-fields"
    const addField = (label, key, area = false) => {
      const field = document.createElement("div")
      field.className = "editor-field"
      const labelNode = document.createElement("label")
      labelNode.textContent = label
      const input = document.createElement(area ? "textarea" : "input")
      if (area) input.rows = 4
      input.value = block.settings?.[key] || ""
      input.dataset.editorPath = `${block.id}.settings.${key}`
      input.dataset.action = "input->public-page-editor#change"
      field.append(labelNode, input)
      fields.append(field)
    }
    addField("Heading", "heading")
    if (block.type !== "companies") addField("Content", "body", true)
    if (block.type === "companies") {
      const search = document.createElement("div")
      search.className = "editor-search"
      search.dataset.editorSearchKind = "companies"
      search.dataset.editorSearchPath = this.searchBaseValue.replace("/alumni", "/companies")
      search.dataset.editorSelectedLabels = "{}"
      search.innerHTML = '<label>Feature companies</label><input type="search" placeholder="Search and select…" data-action="input->public-page-editor#search focus->public-page-editor#search"><div class="editor-search__results" hidden></div><div class="editor-search__selected"></div><small class="editor-search__message">Only organizations represented by alumni experiences appear here.</small>'
      fields.append(search)
    }
    const backgroundField = document.createElement("div")
    backgroundField.className = "editor-field"
    backgroundField.innerHTML = `<label>Section background</label><select data-editor-path="${block.id}.settings.background" data-action="change->public-page-editor#change"><option value="">Default</option><option value="surface">Page background</option><option value="tint">Section background</option><option value="primary">Primary brand</option><option value="accent">Action color</option><option value="secondary">Secondary accent</option></select>`
    backgroundField.querySelector("select").value = block.settings?.background || ""
    fields.append(backgroundField)
    panel.append(fields)
    list.append(row, panel)
  }

  appendPreviewBlock(block) {
    const preview = this.element.querySelector(".public-preview")
    if (!preview) return
    const settings = block.settings || {}
    const node = document.createElement("section")
    node.dataset.previewBlockId = block.id
    const tinted = ["banner", "quote"].includes(block.type)
    const background = this.backgroundKeys.includes(settings.background) ? settings.background : "default"
    node.className = `public-preview__section${tinted ? " public-preview__section--tint" : ""} public-block-background--${background}${block.enabled === false ? " is-hidden" : ""}`
    if (block.type === "companies") {
      const kicker = document.createElement("small"); kicker.textContent = settings.kicker || "COMPANIES"
      const heading = document.createElement("h2"); heading.textContent = settings.heading || "Where our alumni work"
      const list = document.createElement("div"); list.className = "public-company-grid"; list.dataset.previewCollection = "companies"
      node.append(kicker, heading, list)
    } else if (block.type === "quote") {
      const quote = document.createElement("blockquote")
      quote.textContent = `“${settings.body || "A community that stays connected."}”`
      node.append(quote)
    } else {
      const kicker = document.createElement("small")
      kicker.textContent = settings.kicker || block.type.toUpperCase()
      const heading = document.createElement("h2")
      heading.textContent = settings.heading || block.type
      const body = document.createElement("p")
      body.textContent = settings.body || ""
      node.append(kicker, heading, body)
    }
    preview.append(node)
  }

  syncEditorOrder() {
    const list = this.element.querySelector(".public-editor__block-list")
    if (!list) return
    const rows = new Map([...list.querySelectorAll("[data-block-row]")].map((row) => [row.dataset.blockRow, row]))
    const panels = new Map([...list.querySelectorAll("[data-editor-panel]")].map((panel) => [panel.dataset.editorPanel, panel]))
    this.state.blocks.forEach((block) => {
      if (rows.has(block.id)) list.append(rows.get(block.id))
      if (panels.has(block.id)) list.append(panels.get(block.id))
    })
  }

  syncPreviewOrder() {
    const preview = this.element.querySelector(".public-preview")
    if (!preview) return
    const previewBlocks = new Map([...preview.querySelectorAll("[data-preview-block-id]")].map((block) => [block.dataset.previewBlockId, block]))
    this.state.blocks.forEach((block) => {
      if (previewBlocks.has(block.id)) preview.append(previewBlocks.get(block.id))
    })
  }

  updatePreview(path) {
    if (path[0] === "contact") return this.updatePreviewContact()
    if (path[0] === "labels") return this.updatePreviewLabels()
    if (path.length < 3) return
    const block = this.state.blocks.find((item) => item.id === path[0])
    const previewBlock = this.element.querySelector(`[data-preview-block-id="${path[0]}"]`)
    if (!block || !previewBlock) return
    if (path[2] === "background") {
      this.syncPreviewBackground(previewBlock, block.settings?.background)
      return
    }
    if (path[2] === "overlay_color" || path[2] === "overlay_opacity") {
      this.syncHeroOverlay(previewBlock, block.settings)
      return
    }
    const selector = { kicker: "small", headline: "h1", heading: "h2", body: "p,blockquote", cta_label: "b" }[path[2]]
    const target = selector && previewBlock.querySelector(selector)
    if (target) target.textContent = block.settings?.[path[2]] || (path[2] === "body" ? "" : target.textContent)
  }

  updatePreviewLabels() {
    this.element.querySelectorAll("[data-preview-label-key]").forEach((node) => {
      const key = node.dataset.previewLabelKey
      const value = key.split(".").reduce((current, part) => current?.[part], this.state.labels)
      node.textContent = value || this.previewLabelDefaults[key] || node.textContent
    })
  }

  syncPreviewBackground(previewBlock, value) {
    this.backgroundKeys.forEach((key) => previewBlock.classList.remove(`public-block-background--${key}`))
    const key = this.backgroundKeys.includes(value) ? value : "default"
    previewBlock.classList.add(`public-block-background--${key}`)
  }

  syncHeroOverlay(previewBlock, settings = {}) {
    const overlay = previewBlock.querySelector("[data-preview-hero-overlay]")
    if (!overlay) return
    const color = /^#[0-9a-f]{6}$/i.test(settings.overlay_color || "") ? settings.overlay_color : "#101e53"
    const opacity = Math.max(0, Math.min(1, Number.isFinite(Number(settings.overlay_opacity)) ? Number(settings.overlay_opacity) : 0.35))
    overlay.style.backgroundColor = color
    overlay.style.opacity = opacity
  }

  updatePreviewContact() {
    const contact = this.state.contact || {}
    const container = this.element.querySelector("[data-preview-contact]")
    if (!container) return
    const email = container.querySelector("[data-preview-contact-email]")
    const phone = container.querySelector("[data-preview-contact-phone]")
    email.textContent = contact.email || ""
    phone.textContent = contact.phone || ""
    email.classList.toggle("is-hidden", !contact.email)
    phone.classList.toggle("is-hidden", !contact.phone)
    container.classList.toggle("is-hidden", !contact.email && !contact.phone)
  }

  search(event) {
    const input = event.currentTarget
    const container = input.closest(".editor-search")
    if (!container) return
    clearTimeout(this.searchTimers.get(input))
    this.searchTimers.set(input, setTimeout(() => this.loadResults(container, input.value.trim()), 180))
  }

  async loadResults(container, query) {
    const results = container.querySelector(".editor-search__results")
    if (!results) return
    const url = new URL(container.dataset.editorSearchPath, window.location.origin)
    url.searchParams.set("q", query)
    results.hidden = false
    results.replaceChildren(this.resultMessage("Searching…"))
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" })
      const data = await response.json()
      results.replaceChildren(...(data.results || []).map((item) => this.resultButton(container, item)))
      if (!data.results?.length) results.append(this.resultMessage("No matches found."))
    } catch { results.replaceChildren(this.resultMessage("Search unavailable.")) }
  }

  resultButton(container, item) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "editor-search__result"
    button.innerHTML = `<strong></strong><small></small>`
    button.querySelector("strong").textContent = item.label
    button.querySelector("small").textContent = item.meta || ""
    button.addEventListener("click", () => this.selectResult(container, item))
    return button
  }

  selectResult(container, item) {
    const blockId = container.dataset.editorSearchBlock
    const block = this.state.blocks.find((candidate) => candidate.id === blockId)
    if (!block) return
    const kind = container.dataset.editorSearchKind
    const key = { alumni: "profile_ids", alumni_prominent: "featured_profile_id", stories: "post_ids", events: "event_ids", chapters: "community_ids", groups: "community_ids", companies: "organization_ids" }[kind]
    const single = container.dataset.editorSelectionMode === "single"
    block.settings ||= {}
    if (single) block.settings[key] = item.id
    else {
      block.settings[key] ||= []
      if (!block.settings[key].map(String).includes(String(item.id))) block.settings[key].push(item.id)
    }
    const labels = this.labels?.get(container) || {}
    labels[String(item.id)] = item.label
    this.labels ||= new WeakMap()
    this.labels.set(container, labels)
    this.selectionMeta ||= new WeakMap()
    const meta = this.selectionMeta.get(container) || {}
    meta[String(item.id)] = { meta: item.meta || "", title: item.title || "", fieldName: item.field || "", detail: item.detail || "", studyYear: item.study_year || "", bio: item.bio || "", imageUrl: item.image_url || "", excerpt: item.excerpt || "", dateLabel: item.date_label || "", day: item.day || "", timeLabel: item.time_label || "", location: item.location || "", countryCode: item.country_code || "", countryName: item.country_name || "", memberCount: item.member_count || 0 }
    this.selectionMeta.set(container, meta)
    container.querySelector("input").value = ""
    container.querySelector(".editor-search__results").hidden = true
    this.renderSearchSelections()
    this.markDirty()
    this.documentTarget.value = JSON.stringify(this.state)
  }

  renderSearchSelections() {
    this.element.querySelectorAll(".editor-search").forEach((container) => {
      this.labels ||= new WeakMap()
      const labels = this.labels.get(container) || Object.fromEntries(Object.entries(JSON.parse(container.dataset.editorSelectedLabels || "{}")))
      this.labels.set(container, labels)
      const blockId = container.closest("[data-editor-panel]")?.dataset.editorPanel
      const block = this.state.blocks.find((candidate) => candidate.id === blockId)
      const kind = container.dataset.editorSearchKind
      const key = { alumni: "profile_ids", alumni_prominent: "featured_profile_id", stories: "post_ids", events: "event_ids", chapters: "community_ids", groups: "community_ids", companies: "organization_ids" }[kind]
      const single = container.dataset.editorSelectionMode === "single"
      container.dataset.editorSearchBlock = blockId || ""
      const selected = single ? (block?.settings?.[key] ? [block.settings[key]] : []) : (block?.settings?.[key] || [])
      const target = container.querySelector(".editor-search__selected")
      if (!target) return
      target.replaceChildren(...selected.map((id) => {
        const chip = document.createElement("span"); chip.className = "editor-selection-chip"; chip.append(document.createTextNode(labels[String(id)] || String(id).slice(0, 8)))
        const remove = document.createElement("button"); remove.type = "button"; remove.textContent = "×"; remove.setAttribute("aria-label", "Remove selection")
        remove.addEventListener("click", () => { block.settings[key] = single ? null : selected.filter((value) => String(value) !== String(id)); this.renderSearchSelections(); this.markDirty(); this.documentTarget.value = JSON.stringify(this.state) })
        chip.append(remove); return chip
      }))
    })
    this.renderPreviewSelections()
  }

  renderPreviewSelections() {
    const collections = { alumni: { blockType: "featured_alumni", key: "profile_ids" }, stories: { blockType: "stories", key: "post_ids" }, events: { blockType: "events", key: "event_ids", limit: true }, chapters: { blockType: "chapters", key: "community_ids" }, groups: { blockType: "groups", key: "community_ids" }, companies: { blockType: "companies", key: "organization_ids" } }
    this.renderPreviewFeaturedAlumni()
    Object.entries(collections).forEach(([kind, config]) => {
      const target = this.element.querySelector(`[data-preview-collection="${kind}"]`)
      const block = this.state.blocks.find((candidate) => candidate.type === config.blockType)
      if (!target || !block) return
      const selected = block.settings?.[config.key] || []
      const featuredId = kind === "alumni" ? (block.settings?.featured_profile_id || selected[0]) : null
      const visible = (config.limit ? selected.slice(0, Math.max(1, Math.min(12, Number(block.settings?.limit) || 4))) : selected)
        .filter((value) => !featuredId || String(value) !== String(featuredId))
      const search = this.element.querySelector(`.editor-search[data-editor-search-kind="${kind}"]`)
      const labels = search ? (this.labels?.get(search) || {}) : {}
      const meta = search ? (this.selectionMeta?.get(search) || {}) : {}
      const existing = new Map([...target.querySelectorAll("[data-preview-selection-id]")].map((item) => [String(item.dataset.previewSelectionId), item]))
      existing.forEach((item, id) => { if (!visible.some((value) => String(value) === id)) item.remove() })
      visible.forEach((id) => {
        const key = String(id)
        if (existing.has(key) && existing.get(key).isConnected) return
        const label = labels[key] || key.slice(0, 8)
        const item = kind === "alumni" ? this.previewAlumniItem(key, label, meta[key]) : kind === "stories" ? this.previewStoryItem(key, label, meta[key]) : kind === "events" ? this.previewEventItem(key, label, meta[key]) : kind === "companies" ? this.previewCompanyItem(key, label, meta[key]) : this.previewCommunityItem(key, label, meta[key], kind)
        target.insertBefore(item, target.querySelector("[data-preview-empty]"))
      })
      target.querySelectorAll("[data-preview-empty]").forEach((empty) => empty.classList.toggle("is-hidden", visible.length > 0))
    })
  }

  renderPreviewFeaturedAlumni() {
    const showcase = this.element.querySelector("[data-preview-featured-showcase]")
    const block = this.state.blocks.find((candidate) => candidate.type === "featured_alumni")
    if (!showcase || !block) return
    const selected = block.settings?.profile_ids || []
    const id = block.settings?.featured_profile_id || selected[0]
    if (!id) { showcase.classList.add("is-hidden"); return }
    showcase.classList.remove("is-hidden")
    showcase.dataset.previewFeaturedSelectionId = id

    const prominentSearch = this.element.querySelector('.editor-search[data-editor-search-kind="alumni_prominent"]')
    const alumniSearch = this.element.querySelector('.editor-search[data-editor-search-kind="alumni"]')
    const search = block.settings?.featured_profile_id ? (prominentSearch || alumniSearch) : (alumniSearch || prominentSearch)
    const labels = search ? (this.labels?.get(search) || {}) : {}
    const meta = search ? (this.selectionMeta?.get(search) || {}) : {}
    const cachedMeta = this.previewAlumniMeta?.[String(id)] || {}
    const searchedMeta = meta[String(id)] || {}
    const itemMeta = { ...cachedMeta, ...searchedMeta }
    if (cachedMeta.detail) itemMeta.detail = cachedMeta.detail
    ;["detail", "fieldName", "title", "bio", "imageUrl"].forEach((key) => { if (!searchedMeta[key] && cachedMeta[key]) itemMeta[key] = cachedMeta[key] })
    const name = showcase.querySelector("[data-preview-featured-name]")
    const detail = showcase.querySelector("[data-preview-featured-detail]")
    const image = showcase.querySelector("[data-preview-featured-image]")
    const placeholder = showcase.querySelector("[data-preview-featured-placeholder]")
    if (name) name.textContent = labels[String(id)] || String(id).slice(0, 8)
    const detailParts = []
    if (itemMeta.studyYear) detailParts.push(`Class of ${itemMeta.studyYear}`)
    if (itemMeta.fieldName || itemMeta.title) detailParts.push(itemMeta.fieldName || itemMeta.title)
    if (detail && (itemMeta.detail || detailParts.length)) detail.textContent = itemMeta.detail || detailParts.join(" · ")
    if (image && itemMeta.imageUrl) { image.src = itemMeta.imageUrl; image.alt = labels[String(id)] || ""; image.classList.remove("is-hidden") }
    if (placeholder) { placeholder.textContent = (labels[String(id)] || "Alumni").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase(); placeholder.classList.toggle("is-hidden", Boolean(itemMeta.imageUrl)) }
    const quote = showcase.querySelector("[data-preview-featured-quote]")
    const role = showcase.querySelector("[data-preview-featured-role]")
    const copy = showcase.querySelector("[data-preview-featured-showcase-copy]")
    const body = showcase.querySelector("[data-preview-featured-showcase-body]")
    if (quote && !block.settings?.featured_quote && itemMeta.bio) quote.textContent = itemMeta.bio
    if (role && !block.settings?.featured_role && (itemMeta.title || itemMeta.fieldName)) role.textContent = itemMeta.title || itemMeta.fieldName
    if (body && !block.settings?.showcase_body && itemMeta.bio) body.textContent = itemMeta.bio
    if (copy && !block.settings?.showcase_quote && itemMeta.bio) copy.textContent = document.documentElement.lang === "fr" ? "« Le parcours continue bien au-delà du diplôme. »" : "The journey continues well beyond graduation."
  }

  previewAlumniItem(id, label, meta) {
    const card = document.createElement("article")
    card.className = "public-profile-card public-profile-card--horizontal"
    card.dataset.previewSelectionId = id
    const cachedMeta = this.previewAlumniMeta?.[String(id)] || {}
    const itemMeta = { ...cachedMeta, ...((meta && typeof meta === "object" ? meta : null) || {}) }
    const imageUrl = itemMeta.imageUrl || ""
    const initials = imageUrl ? document.createElement("img") : document.createElement("div")
    initials.className = imageUrl ? "public-profile-card__image" : "public-profile-card__placeholder"
    if (imageUrl) { initials.src = imageUrl; initials.alt = label } else initials.textContent = label.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()
    const body = document.createElement("div")
    body.className = "public-profile-card__body"
    const name = document.createElement("strong")
    name.textContent = label
    const type = document.createElement("span")
    type.textContent = itemMeta.title || itemMeta.fieldName || itemMeta.meta || "Alumni"
    body.append(name, type)
    if (itemMeta.bio?.trim()) {
      const bio = document.createElement("p")
      bio.textContent = itemMeta.bio
      body.append(bio)
    }
    card.append(initials, body)
    return card
  }

  previewCommunityItem(id, label, meta, kind) {
    const item = document.createElement("div")
    item.className = `public-community-card ${kind === "groups" ? "public-group-card" : "public-chapter-card"}`
    item.dataset.previewSelectionId = id
    const identity = document.createElement("div")
    identity.className = "public-community-card__identity"
    const details = document.createElement("div")
    const name = document.createElement("strong")
    name.textContent = label
    const subtitle = document.createElement("span")
    const info = typeof meta === "object" && meta ? meta : { meta: meta || "", memberCount: 0 }
    if (kind === "groups") {
      const icon = document.createElement("span")
      icon.className = "public-community-card__icon"
      icon.setAttribute("aria-hidden", "true")
      icon.textContent = "◎"
      identity.append(icon)
      subtitle.textContent = "Interest group"
    } else {
      if (info.countryCode) {
        const flag = document.createElement("img")
        flag.className = "public-community-card__flag"
        flag.alt = ""
        flag.src = `https://flagcdn.com/${String(info.countryCode).toLowerCase()}.svg`
        identity.append(flag)
      }
      subtitle.textContent = info.countryName || info.meta || "Chapter"
    }
    details.append(name, subtitle)
    identity.append(details)
    const members = document.createElement("div")
    members.className = "public-community-card__members"
    const count = document.createElement("strong")
    count.textContent = Number(info.memberCount || 0).toLocaleString()
    const countLabel = document.createElement("span")
    countLabel.textContent = "Members"
    members.append(count, countLabel)
    item.append(identity, members)
    return item
  }

  previewCompanyItem(id, label, meta) {
    const item = document.createElement("div")
    item.className = "public-company-card"
    item.dataset.previewSelectionId = id
    const info = typeof meta === "object" && meta ? meta : {}
    if (info.imageUrl) { const image = document.createElement("img"); image.src = info.imageUrl; image.alt = ""; image.className = "public-company-card__logo"; item.append(image) }
    const name = document.createElement("strong"); name.textContent = label; item.append(name)
    return item
  }

  previewStoryItem(id, label, meta) {
    const card = document.createElement("article")
    card.className = "public-story-card"
    card.dataset.previewSelectionId = id
    const imageUrl = typeof meta === "object" ? meta.imageUrl : ""
    const media = document.createElement("div")
    media.className = "public-story-card__media"
    let mediaNode = media
    if (imageUrl) {
      const image = document.createElement("img")
      image.src = imageUrl
      image.alt = ""
      image.className = "public-story-card__media"
      mediaNode = image
    } else {
      media.classList.add("public-story-card__media--fallback")
      const logo = document.createElement("img")
      logo.src = this.element.querySelector(".public-preview")?.dataset.previewFallbackLogo || "/assets/axis-logo.svg"
      logo.alt = ""
      logo.className = "public-story-card__fallback-logo"
      media.append(logo)
    }
    const body = document.createElement("div")
    body.className = "public-story-card__body"
    const title = document.createElement("h3")
    title.textContent = label
    const excerpt = document.createElement("p")
    excerpt.textContent = (typeof meta === "object" ? meta.excerpt : "") || (typeof meta === "object" ? meta.meta : meta) || "Community story"
    const more = document.createElement("span")
    more.className = "public-text-link public-story-card__more"
    more.textContent = "Read more"
    body.append(title, excerpt, more)
    card.append(mediaNode, body)
    return card
  }

  previewEventItem(id, label, meta) {
    const item = document.createElement("article")
    item.className = "public-event-card"
    item.dataset.previewSelectionId = id
    const date = document.createElement("div")
    date.className = "public-event-date"
    const month = document.createElement("strong")
    month.textContent = (typeof meta === "object" ? meta.dateLabel : "") || "EVENT"
    const day = document.createElement("span")
    day.textContent = (typeof meta === "object" ? meta.day : "") || ""
    date.append(month, day)
    const details = document.createElement("div")
    const title = document.createElement("h3")
    title.textContent = label
    const info = document.createElement("p")
    info.textContent = (typeof meta === "object" ? meta.timeLabel : meta) || "Upcoming event"
    if (typeof meta === "object" && meta.location) info.textContent += ` · ${meta.location}`
    details.append(title, info)
    item.append(date, details)
    return item
  }

  resultMessage(text) { const node = document.createElement("span"); node.className = "editor-search__message"; node.textContent = text; return node }
}
